
export interface BlogPost {
  id: string;
  title: string;
  summary: string;
  date: string;
  content: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: 'compound-interest-magic',
    title: '복리의 마법: 사회초년생이 1억을 가장 빨리 모으는 현실적인 방법',
    summary: '사회초년생이 1억을 가장 빨리 모으는 현실적인 방법, 복리의 마법을 통해 투자의 미래 가치를 시뮬레이션하고 부의 성장을 계획해보세요.',
    date: '2025년 7월 22일',
    content: `
## 복리의 마법: 사회초년생이 1억을 가장 빨리 모으는 현실적인 방법

이제 막 첫 월급을 받고, 막연하게 '돈을 모아야지'라고 생각하고 계신가요? '1억'이라는 숫자는 너무나 멀게만 느껴지고, 어디서부터 시작해야 할지 막막할 수 있습니다. 하지만 괜찮습니다. 우리에겐 아인슈타인이 '세계 8대 불가사의'라고 불렀던, 세상에서 가장 강력한 무기가 있으니까요. 바로 **복리(複利)** 입니다.

### 복리란 무엇인가? 눈덩이처럼 불어나는 내 돈

복리를 가장 쉽게 설명하면 '**이자에 이자가 붙는 방식**'입니다.

*   **단리:** 원금 100만 원에 대한 이자만 계속 받는 방식
    *   1년 후: 원금(100) + 이자(10) = 110만 원
    *   2년 후: 원금(100) + 이자(10) + 이자(10) = 120만 원
*   **복리:** 원금과 이자를 합친 돈에 대한 이자를 받는 방식
    *   1년 후: 원금(100) + 이자(10) = 110만 원
    *   2년 후: 원금+이자(110) + 이자(11) = 121만 원

겨우 1만 원 차이라고요? 이 작은 차이가 바로 가파른 언덕에서 작은 눈덩이를 굴리는 것과 같습니다. 처음에는 미미하지만, 계속 굴러가다 보면 누구도 막을 수 없는 거대한 눈사태가 되죠.

### 왜 사회초년생에게 복리가 가장 강력한 무기일까?

복리 효과를 극대화하는 가장 중요한 변수는 바로 '**시간**'입니다. 그리고 사회초년생은 바로 그 '시간'이라는 가장 강력한 자산을 가지고 있습니다.

예를 들어 볼까요? 매월 50만 원씩, 연평균 8% 수익률의 자산에 투자한다고 가정해 봅시다.

*   **A (25세 시작):** 60세까지 35년간 꾸준히 투자하면?
    *   총 원금: 2억 1,000만 원
    *   **최종 자산: 약 11억 7,000만 원**
*   **B (35세 시작):** 똑같이 60세까지, 10년 늦게 25년간 투자하면?
    *   총 원금: 1억 5,000만 원
    *   **최종 자산: 약 4억 7,000만 원**

단 10년 늦게 시작했을 뿐인데, 최종 자산은 7억 원이라는 엄청난 차이가 벌어집니다. 이것이 바로 '시간의 힘'이자 복리의 마법입니다.

### 1억 만들기, 현실적인 액션 플랜

그럼 이제 무엇을 해야 할까요? 거창한 계획은 필요 없습니다.

1.  **지금 당장 시작하기:** 한 달에 5만 원, 10만 원이라도 좋습니다. 금액의 크기보다 중요한 것은 '오늘' 투자를 시작해 복리라는 눈덩이를 굴리기 시작하는 것입니다.
2.  **꾸준히 투자하기:** 월급날, 일정 금액이 자동으로 투자되도록 설정하세요. 주가가 오르든 내리든 꾸준히 사 모으는 '적립식 투자'는 장기적으로 매우 강력한 전략입니다.
3.  **연 7~10% 수익률 목표:** "어디에 투자해야 그 수익률이 나지?"라고 생각할 수 있습니다. 은행 예금으로는 어렵습니다. 하지만 미국 S&P 500이나 나스닥 100 지수를 추종하는 ETF에 장기적으로 투자한다면, 역사적으로 연평균 7~10%의 수익률은 충분히 기대해 볼 수 있는 수치입니다.

### 직접 당신의 미래를 시뮬레이션 해보세요

말로만 듣는 복리, 직접 눈으로 확인해 보세요. 여러분의 월 저축액과 예상 수익률을 입력하면, 내 돈이 1억 원, 10억 원이 되는 시점을 바로 시뮬레이션해볼 수 있습니다. 미래의 부유한 당신을 미리 만나보세요.

**[복리 계산기 바로가기](/compound)**

**결론:**
투자의 대가 워렌 버핏의 자산 대부분은 60세 이후에 형성되었습니다. 그가 평생에 걸쳐 굴려온 복리의 눈덩이가 노년에 폭발한 것이죠. 우리에게는 그보다 훨씬 더 많은 '시간'이 있습니다.

가장 중요한 것은 완벽한 때를 기다리는 것이 아니라, **'오늘' 시작하는 것입니다.**
    `,
  },
  {
    id: 'us-etf-guide',
    title: '왕초보를 위한 미국 ETF 투자 완벽 가이드 (S&P 500 vs 나스닥 100)',
    summary: '왕초보 투자자를 위한 미국 ETF 투자 가이드. S&P 500과 나스닥 100 ETF의 특징을 비교하고, 왜 초보자에게 가장 확실한 투자 방법인지 설명합니다.',
    date: '2025년 7월 23일',
    content: `
## 왕초보를 위한 미국 ETF 투자 완벽 가이드 (S&P 500 vs 나스닥 100)

'복리의 마법'에 대해 알게 된 당신, 이제 마음속에는 하나의 질문이 남았을 겁니다. "그래서, 그 좋은 복리 효과를 어디에 투자해야 누릴 수 있는데?" 투자의 세계는 너무나 넓고 복잡해 보입니다. 하지만 걱정 마세요. 투자의 대가 워렌 버핏이 아내에게 유언으로 남겼을 정도로, 초보자에게 가장 확실하고 강력한 방법이 있습니다. 바로 **미국 지수 추종 ETF**입니다.

### ETF, 왜 초보자의 '치트키'일까?

ETF(Exchange Traded Fund, 상장지수펀드)를 가장 쉽게 설명하면 '**주식 모음 바구니**'입니다. 삼성전자, 애플 같은 개별 기업의 주식을 하나하나 고르는 대신, 유망한 기업 수십, 수백 개를 모아놓은 바구니를 통째로 사는 것이죠.

*   **자동으로 분산 투자:** '계란을 한 바구니에 담지 말라'는 격언처럼, ETF 하나만 사도 수백 개 기업에 자동으로 분산 투자되어 안정성이 크게 올라갑니다.
*   **낮은 수수료:** 전문가가 운용하는 펀드에 비해 운용 보수가 매우 저렴하여, 내 소중한 수익률을 갉아먹지 않습니다.
*   **편리한 거래:** 일반 주식처럼 스마트폰 증권 앱으로 언제든지 쉽게 사고팔 수 있습니다.

### S&P 500 vs. 나스닥 100: 세기의 대결

미국 지수 ETF 중 가장 대표적인 두 '바구니'가 바로 S&P 500과 나스닥 100입니다.

| 구분 | **S&P 500 ETF (예: VOO, IVV)** | **나스닥 100 ETF (예: QQQ)** |
| :--- | :--- | :--- |
| **컨셉** | **'미국 대표팀'** | **'기술 올스타팀'** |
| **구성** | 미국 우량 기업 500개 (금융, 헬스케어, IT 등 전 분야) | 나스닥 상장 기업 중 금융주를 제외한 상위 100개 (IT 기술주 비중이 매우 높음) |
| **특징** | 안정적이고 꾸준한 성장. 미국 경제 전체와 함께 움직임. | 폭발적인 성장 잠재력. 기술주의 성장에 크게 베팅. 변동성이 더 큼. |
| **추천** | "나는 안정성이 제일 중요해." | "나는 기술의 미래에 투자하고 싶어." |

### 그래서, 어떻게 사는 건데? (실전편)

1.  **증권 계좌 개설:** 해외 주식 거래가 가능한 증권사 앱을 통해 비대면으로 계좌를 만듭니다.
2.  **ETF 검색:** 증권 앱 검색창에 사고 싶은 ETF의 티커(종목 코드)를 입력합니다. (예: \`VOO\`, \`QQQ\`)
3.  **매수:** 원하는 수량만큼 일반 주식을 사듯이 매수하면 끝입니다.

가장 중요한 것은 거창한 분석이 아닙니다. 매월 월급날, 5만 원이든 10만 원이든 꾸준히 사 모으는 '적립식 투자'를 통해 **'시간'과 '복리'를 내 편으로 만드는 것**입니다.
    `,
  },
  {
    id: 'ai-vibe-coding',
    title: 'AI와 함께 코딩하기: 게임 쿠폰 페이지를 만들며 느낀 새로운 개발 경험',
    summary: 'AI 개발 도우미 Gemini CLI와 함께 게임 쿠폰 페이지를 만들며 경험한 새로운 개발 방식, \'바이브 코딩\'에 대한 이야기입니다.',
    date: '2025년 7월 24일',
    content: `
## AI와 함께 코딩하기: 게임 쿠폰 페이지를 만들며 느낀 새로운 개발 경험

모든 개발자는 머릿속 아이디어가 즉시 코드가 되는 상상을 합니다. 불필요한 과정 없이, 오직 창의적인 설계와 문제 해결에만 몰두하는 그런 순간을 말이죠. 저 역시 그랬습니다.

최근 한 게임의 반복적인 쿠폰 등록 작업에서 작은 불편함을 느꼈고, 이 아이디어를 해결하기 위해 평소와 다른 방법을 시도해 보기로 했습니다. 바로 AI 개발 도우미, **Gemini CLI**와 함께하는, 요새 말하는 '**바이브 코딩(Vibe-based Coding)**'이었습니다. 그리고 이 경험은 저의 개발 방식을, 어쩌면 개발에 대한 생각 자체를 바꿔놓았습니다.

### '과정'이 아닌 '결과'에 집중하는 새로운 경험

기존의 개발 방식은 과정의 연속이었습니다. 코드 컨벤션, 유지보수성, 기록 등 결과물에 도달하기까지 수많은 중간 과정을 챙겨야 했죠. 하지만 AI와의 협업은 달랐습니다. 저는 처음으로 과정이 아닌 **오직 '결과물'에만 집중**하는 새로운 경험을 했습니다.

마치 지식이 무한한 동료와 함께 **페어 프로그래밍(Pair Programming)** 을 하는 것 같았습니다. 저는 터미널을 열고 '대화'를 시작했습니다.

> **나:** "페이지의 기본 레이아웃이 필요해. 제목과 리스트를 담을 영역으로 구성해 줘."
>
> **Gemini CLI:** (잠시 후, 필요한 UI 구조와 코드 뼈대를 생성)

신기한 점은, 대화를 이어갈수록 더 정확한 결과물을 얻기 위해 **'어떤 단어를 써야 AI가 더 잘 알아들을까?'** 하고 제가 먼저 고민하게 된다는 것이었습니다. 이것은 단순히 명령을 내리는 것을 넘어, AI와 소통하며 **정확한 인식을 맞춰가는(align)** 과정이었습니다.

### 개발을 아는 사람이 AI를 더 잘 쓸 수밖에 없는 이유

물론 AI가 모든 것을 완벽하게 해결해 주진 않았습니다. 가끔은 제가 직접 코드를 수정해야 하는 순간도 찾아왔죠. 하지만 바로 그 지점에서 저는 확신했습니다. **개발을 잘 아는 사람일수록 '바이브 코딩'을 더 잘할 수밖에 없다는 것을요.**

AI는 강력한 도구이지만, 최종 결과물의 완성도를 결정하는 것은 결국 개발자의 통찰력과 경험입니다. AI가 생성한 코드의 방향이 맞는지 판단하고, 부족한 부분을 채워 넣으며, 전체적인 구조를 조율하는 역할은 오롯이 개발자의 몫이었습니다.

### 개발의 '구현'을 넘어 '설계'의 영역으로

AI와의 협업은 개발 과정에서 가장 지루하고 반복적인 부분(설정, 검색, 코드 타이핑)을 극적으로 줄여줍니다. 그 결과, 개발자는 비로소 가장 중요한 것, 바로 '**아이디어를 현실로 만드는 창의적인 과정**'에 온전히 집중할 수 있게 됩니다.

이 쿠폰 페이지는 단순히 게임 아이템을 얻기 위한 도구가 아니라, AI와 함께 '바이브'를 맞춰가며 개발하는 것이 얼마나 즐거운 경험인지 보여주는 저의 작은 증거입니다.

만약 당신의 머릿속에 '이런 게 있으면 좋겠다' 싶은 아이디어가 잠자고 있다면, 지금 바로 AI 개발 파트너에게 말을 걸어보세요. 생각보다 훨씬 더 빠르고, 즐겁게 당신의 아이디어를 현실로 만들 수 있을 겁니다.

**[제가 만든 결과물을 직접 확인해보세요: 쿠폰 페이지](/coupon/seven-knights)**
    `,
  },
  {
    id: 'pension-fund-vs-irp',
    title: '연금저축펀드 vs. IRP, 사회초년생을 위한 최고의 절세 투자법 (2025년 기준)',
    summary: '첫 월급의 기쁨도 잠시, 통장을 스쳐 지나가는 숫자를 보며 한숨 쉬어본 적 없으신가요? \'세금\'은 너무 어렵고, \'연말정산\'은 매년 돌아오는 낯선 숙제 같습니다. 하지만 여기, 국가가 "이걸로 투자하면 세금 깎아줄게!"라고 공인한 강력한 \'절세 치트키\'가 있습니다. 바로 연금저축펀드와 IRP입니다. 이 글 하나로 두 제도의 차이점과 나에게 맞는 최고의 조합을 확실하게 알아가세요.',
    date: '2025년 8월 10일',
    content: `
## 연금저축펀드 vs. IRP, 사회초년생을 위한 최고의 절세 투자법 (2025년 기준)

첫 월급의 기쁨도 잠시, 통장을 스쳐 지나가는 숫자를 보며 한숨 쉬어본 적 없으신가요? '세금'은 너무 어렵고, '연말정산'은 매년 돌아오는 낯선 숙제 같습니다. 하지만 여기, 국가가 "이걸로 투자하면 세금 깎아줄게!"라고 공인한 강력한 '절세 치트키'가 있습니다. 바로 **연금저축펀드**와 **IRP**입니다. 이 글 하나로 두 제도의 차이점과 나에게 맞는 최고의 조합을 확실하게 알아가세요.

### 세금, 그 무서운 복리 효과의 적

우리는 지난 글에서 '복리의 마법'이 시간을 먹고 자라난다는 것을 배웠습니다. 하지만 세금은 그 마법을 방해하는 가장 큰 적입니다. 일반 계좌에서 ETF나 펀드로 100만 원의 수익을 내면, 우리는 15.4%인 15만 4천 원을 세금으로 내야 합니다. 이 돈이 20년, 30년 쌓이면 복리 효과는 눈에 띄게 줄어들죠. 절세 계좌는 바로 이 세금을 아껴서, 내 돈이 더 큰 눈덩이가 되도록 굴려주는 역할을 합니다.

### 국가가 밀어주는 절세 계좌: 연금저축펀드와 IRP

두 제도는 모두 연금 수령을 목적으로 하는 장기 투자 계좌이며, 매년 납입한 금액에 대해 강력한 **세액공제** 혜택을 제공한다는 공통점이 있습니다. 연말정산 때 '13월의 월급'을 만들어주는 핵심이죠.

### 연금저축펀드 vs. IRP, 무엇이 다른가? (핵심 비교)

그렇다면 둘의 차이점은 무엇일까요? 이 표 하나로 모든 것이 정리됩니다.

| 구분 | **연금저축펀드** | **IRP (개인형 퇴직연금)** |
| :--- | :--- | :--- |
| **가입 자격** | **누구나** 가능 (소득 무관) | 소득이 있는 **근로자/자영업자** |
| **납입 한도** | 연 1,800만 원 | 연 1,800만 원 (연금저축 포함) |
| **세액공제 한도** | **연 600만 원** | **연 900만 원** (연금저축 포함) |
| **투자 가능 상품** | **자유로움** (펀드, ETF 등 100% 공격적 투자 가능) | **제한적** (안전 자산 30% 의무 편입 규칙) |
| **중도 인출** | 비교적 자유로움 (세금 불이익 감수 시) | **매우 어려움** (법정 사유 외 사실상 불가) |

### 그래서, 실제로 얼마를 돌려받는 건데? (가장 중요!)

"세액공제"라는 말이 어렵게 느껴지시나요? 간단히 말해 "**연말정산 때 국가가 내 계좌로 현금을 되돌려주는 것**"입니다. 환급액은 내 연봉에 따라 달라집니다.

* **총 급여 5,500만 원 이하:** 납입액의 **16.5%** 환급
* **총 급여 5,500만 원 초과:** 납입액의 **13.2%** 환급

만약 당신이 IRP를 활용해 세액공제 한도인 **연 900만 원**을 꽉 채웠다면?

* (연봉 5,500만 원 이하) → **148만 5,000원**을 돌려받습니다.
* (연봉 5,500만 원 초과) → **118만 8,000원**을 돌려받습니다.

이것은 리스크 없는 **연 13.2% ~ 16.5%짜리 '확정 수익' 예금**과 같습니다. 시중 은행 예금 금리가 3%대인 것을 감안하면, 하지 않을 이유가 없는 엄청난 혜택이죠.

**실제로 저, 이 사이트의 운영자도 이 혜택을 알게 된 후로는 매년 900만 원 한도를 꽉 채워서 투자하고 있습니다. 연말정산 때마다 100만 원이 훌쩍 넘는 돈이 그대로 계좌에 다시 들어오는 경험은, 투자를 계속하게 만드는 가장 강력한 동기부여가 됩니다.**

### 그래서 나는 뭘 선택해야 할까? (상황별 추천)

*   **"투자가 처음이고, 자유로운 투자를 원한다면?" → 연금저축펀드부터!**
    가입 조건이 없고, ETF 등에 100% 자유롭게 투자할 수 있는 연금저축펀드로 시작하는 것이 좋습니다.

*   **"나는 이미 직장인이고, 세액공제 혜택을 최대로 받고 싶다면?" → IRP를 조합하거나 IRP만!**
    연금저축펀드의 세액공제 한도(600만 원)를 넘어, 최대 900만 원까지 혜택을 받고 싶다면 IRP 계좌가 반드시 필요합니다.

*   **가장 좋은 전략은? → 둘 다 만들고, 한도 꽉 채우기!**
    총 900만 원의 세액공제 혜택을 최대로 누리고 싶다면, 연금저축펀드에 600만 원, IRP에 300만 원을 납입하는 것이 일반적인 최적의 조합입니다.

### 지금 바로 시작해야 하는 이유

절세 혜택은 그 해에 납입한 금액을 기준으로 합니다. 올해가 가기 전에 계좌를 만들고 단 10만 원이라도 납입해야, 올해의 세금 혜택을 받을 수 있습니다. "내년에 해야지"라고 미루는 것은 1년 치 세금을 그대로 버리는 것과 같습니다. 지금 바로 스마트폰의 증권사 앱을 켜고, '연금저축펀드'와 'IRP' 계좌부터 만들어보세요. 당신의 미래가 달라집니다.
    `
  },
  {
    id: 'developer-investment-portfolio',
    title: '개발자인 내가 나만의 투자 포트폴리오를 관리하는 법',
    summary: "개발자에게 시간은 금, 투자는 미래입니다. 저는 두 가지 재미있는 목표를 가지고 꾸준히 돈을 모으고 있습니다. 바로 '금괴 1개'와 '1 비트코인'을 온전히 소유하는 것입니다. 이 구체적인 목표가 있기에, 복잡한 투자를 단순한 시스템으로 만들고 꾸준히 관리하는 과정이 더욱 즐거워집니다. 이 글을 통해 저의 자산 배분 원칙부터, 종목 선정 및 비중 관리 방법까지 모든 것을 공유합니다.",
    date: '2025년 8월 11일',
    content: `
## 개발자인 내가 나만의 투자 포트폴리오를 관리하는 법

개발자에게 시간은 금, 투자는 미래입니다. 저는 두 가지 재미있는 목표를 가지고 꾸준히 돈을 모으고 있습니다. 바로 '**금괴 1개**'와 '**1 비트코인**'을 온전히 소유하는 것입니다. 이 구체적인 목표가 있기에, 복잡한 투자를 단순한 시스템으로 만들고 꾸준히 관리하는 과정이 더욱 즐거워집니다. 이 글을 통해 저의 자산 배분 원칙부터, 종목 선정 및 비중 관리 방법까지 모든 것을 공유합니다.

### 나의 자산 배분: 70:15:15 규칙과 최종 목표

모든 투자의 시작은 자산을 어떻게 나눌지 결정하는 것에서 출발합니다. 저의 원칙은 간단합니다.

* **주식 (70%):** 세계 경제의 성장을 믿고, 꾸준히 우상향하는 자산에 가장 큰 비중을 둡니다.
* **금 (15%) & 비트코인 (15%):** 나머지 30%는 저의 최종 목표인 **'금괴'와 '1 비트코인'** 을 모으기 위해 각각 투자합니다. 이 자산들은 주식과 다른 방향으로 움직이는 경향이 있어 포트폴리오의 안정성을 더해주는 동시에, 제게는 투자를 게임처럼 즐겁게 만들어주는 '수집 아이템'과 같습니다.

### 투자의 엔진: 절세 계좌부터 채우기

수익을 내는 것만큼 중요한 것은 '세금'이라는 비용을 줄이는 것입니다. 저는 모든 투자의 시작을 연금저축펀드와 IRP라는 강력한 절세 계좌에서 시작합니다.

* **연금저축펀드 활용법:** \`S&P 500 ETF (85%)\`를 중심으로 미국 시장 전체의 성장을 따라가면서, \`인도 ETF (15%)\`로 신흥국 성장 가능성에 일부를 투자합니다.
* **IRP 활용법:** 위험자산 한도(70%)는 \`미국 배덩주 ETF\`로 채워 꾸준한 현금 흐름을 만들고, 나머지(30%)는 채권 등이 포함된 \`혼합형 ETF\`로 안정성을 더합니다.

### 투자 철학의 핵심: "내가 쓰는 만큼 투자한다"

절세 계좌를 모두 채운 후, 남은 돈으로 투자할 개별 주식은 어떻게 고를까요? 저는 전설적인 투자자 피터 린치의 "네가 아는 것에 투자하라"는 말을 개발자의 방식으로 재해석했습니다. 바로 '**내가 쓰는 만큼 투자한다**'는 원칙입니다.

**나만의 투자 비중 계산법:**
1.  **기준 1:** \`내가 1년 동안 해당 기업의 서비스/상품에 사용하는 비용\`
2.  **기준 2:** \`내가 하루 동안 해당 기업의 서비스/상품을 사용하는 시간\`

저는 이 두 가지 기준을 **Google Sheets**에 정리하고, 점수화하여 각 개별 주식의 투자 비중을 객관적으로 결정합니다. 예를 들어, 저는 매일 iPhone(Apple)을 8시간 사용하고, YouTube(Google)는 2시간 봅니다. 이 데이터를 바탕으로 제 포트폴리오에서 Apple의 비중이 Google보다 높게 유지되는 식입니다.

### 결론: 당신만의 '투자 알고리즘'과 '즐거운 목표'를 만드세요

투자는 정답이 있는 것이 아니라, 자신만의 원칙과 시스템을 만들어가는 과정입니다. 특히 '금괴 모으기'처럼 자신만의 즐거운 목표를 설정하는 것은 투자를 지치지 않고 계속하게 만드는 최고의 원동력입니다.

개발자로서 데이터를 정리하고, 자신만의 기준을 만들어 투자에 접근하는 것은 스트레스가 아닌, 또 하나의 즐거운 사이드 프로젝트가 될 수 있습니다.
`
  }
];

